import { Box, Paper, Button, Alert } from "@mui/material";
import CustomTable, {
  type CustomTableColumn,
} from "components/common/CustomTable";
import RowActions from "components/common/RowActions";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import { isValid } from "date-fns";
import { usePatients } from "hooks/usePatients";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { Patient } from "types/patient";
import { formatBirthDate, formatDate } from "utils/date";
import { formatPatientName } from "utils/patient";

import { practicePatientManagementApi } from "../../../apis";
import {
  PatientsPaginationRequestDtoGenderEnum,
  PatientsPaginationRequestDtoStatusEnum,
  PatientsPaginationRequestDtoSortByEnum,
  PatientsPaginationRequestDtoSortOrderEnum,
} from "../../../libs/cellera-sdk/models";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";
import CancelPatientRequestModal from "./CancelPatientRequestModal";
import PatientFilters from "./PatientFilters";
import PatientRequestModal from "./PatientRequestModal";
import PatientSearch from "./PatientSearch";
import RequestPatientConfirmModal from "./RequestPatientConfirmModal";

interface PatientFilters {
  search: string;
  gender: PatientsPaginationRequestDtoGenderEnum[];
  status: PatientsPaginationRequestDtoStatusEnum[];
  ageFrom: number | null;
  ageTo: number | null;
  sortBy: string;
  sortOrder: "asc" | "desc";
  page: number;
  pageSize: number;
}

const PatientManagement: React.FC = () => {
  const { colors } = useCelleraTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isPatientRequestModalOpen, setIsPatientRequestModalOpen] =
    useState(false);
  const [isCancelRequestModalOpen, setIsCancelRequestModalOpen] =
    useState(false);
  const [selectedPatientForCancel, setSelectedPatientForCancel] =
    useState<Patient | null>(null);
  const [isRequestConfirmModalOpen, setIsRequestConfirmModalOpen] =
    useState(false);
  const [selectedPatientForRequest, setSelectedPatientForRequest] =
    useState<Patient | null>(null);

  // Initialize filters from URL params or defaults
  const [filters, setFilters] = useState<PatientFilters>(() => {
    const ageFromParam = searchParams.get("ageFrom");
    const ageToParam = searchParams.get("ageTo");

    return {
      search: searchParams.get("search") || "",
      gender:
        (searchParams
          .get("gender")
          ?.split(",")
          .filter(Boolean) as PatientsPaginationRequestDtoGenderEnum[]) || [],
      status:
        (searchParams
          .get("status")
          ?.split(",")
          .filter(Boolean) as PatientsPaginationRequestDtoStatusEnum[]) || [],
      ageFrom: ageFromParam ? parseInt(ageFromParam, 10) : null,
      ageTo: ageToParam ? parseInt(ageToParam, 10) : null,
      sortBy: searchParams.get("sortBy") || "requestedDate",
      sortOrder: (searchParams.get("sortOrder") as "asc" | "desc") || "desc",
      page: parseInt(searchParams.get("page") || "0", 10),
      pageSize: parseInt(searchParams.get("pageSize") || "10", 10),
    };
  });

  // Debounced search state
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  // Debounce search input with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Update URL when filters change
  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    if (filters.search) newSearchParams.set("search", filters.search);
    if (filters.gender.length > 0)
      newSearchParams.set("gender", filters.gender.join(","));
    if (filters.status.length > 0)
      newSearchParams.set("status", filters.status.join(","));
    if (filters.ageFrom !== null)
      newSearchParams.set("ageFrom", filters.ageFrom.toString());
    if (filters.ageTo !== null)
      newSearchParams.set("ageTo", filters.ageTo.toString());
    if (filters.sortBy !== "requestedDate")
      newSearchParams.set("sortBy", filters.sortBy);
    if (filters.sortOrder !== "desc")
      newSearchParams.set("sortOrder", filters.sortOrder);
    if (filters.page !== 0)
      newSearchParams.set("page", filters.page.toString());
    if (filters.pageSize !== 10)
      newSearchParams.set("pageSize", filters.pageSize.toString());

    setSearchParams(newSearchParams, { replace: true });
  }, [filters, setSearchParams]);

  const birthDateRange = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();

    let birthDateFrom: string | undefined;
    let birthDateTo: string | undefined;

    if (filters.ageTo !== null) {
      const yearFrom = currentYear - filters.ageTo;
      birthDateFrom = formatDate(new Date(yearFrom, currentMonth, currentDay));
    }

    if (filters.ageFrom !== null) {
      const yearTo = currentYear - filters.ageFrom;
      birthDateTo = formatDate(new Date(yearTo, currentMonth, currentDay));
    }

    return { birthDateFrom, birthDateTo };
  }, [filters.ageFrom, filters.ageTo]);

  // Use the patients API hook with filters
  const { patients, totalCount, loading, error, refetch } = usePatients({
    page: filters.page,
    pageSize: filters.pageSize,
    search: debouncedSearch || undefined,
    gender: filters.gender.length > 0 ? filters.gender : undefined,
    status: filters.status.length > 0 ? filters.status : undefined,
    birthDateFrom: birthDateRange.birthDateFrom,
    birthDateTo: birthDateRange.birthDateTo,
    sortBy: filters.sortBy as PatientsPaginationRequestDtoSortByEnum,
    sortOrder:
      filters.sortOrder === "asc"
        ? PatientsPaginationRequestDtoSortOrderEnum.Asc
        : PatientsPaginationRequestDtoSortOrderEnum.Desc,
  });

  const handlePatientRequest = () => {
    setIsPatientRequestModalOpen(true);
  };

  const handlePatientRequestSubmit = useCallback(
    async (email: string) => {
      await practicePatientManagementApi.requestPatientDataSharing({
        requestPatientDataSharingDto: { email },
      });

      refetch();
    },
    [refetch]
  );

  const handleViewPatientDetails = useCallback(
    (patientId: string) => {
      navigate(EPath.PATIENT_DASHBOARD.replace(":patientId", patientId));
    },
    [navigate]
  );

  const handleCancelRequest = useCallback((patient: Patient) => {
    setSelectedPatientForCancel(patient);
    setIsCancelRequestModalOpen(true);
  }, []);

  const handleCancelRequestSubmit = useCallback(async () => {
    if (!selectedPatientForCancel?.patientId) return;

    await practicePatientManagementApi.cancelDataSharingRequests({
      cancelDataSharingRequestsDto: {
        patientIds: [selectedPatientForCancel.patientId],
      },
    });

    refetch();
  }, [selectedPatientForCancel, refetch]);

  const handleRequest = useCallback((patient: Patient) => {
    setSelectedPatientForRequest(patient);
    setIsRequestConfirmModalOpen(true);
  }, []);

  const handleRequestSubmit = useCallback(async () => {
    if (!selectedPatientForRequest?.email) return;

    await practicePatientManagementApi.requestPatientDataSharing({
      requestPatientDataSharingDto: {
        email: selectedPatientForRequest.email,
      },
    });

    refetch();
  }, [selectedPatientForRequest, refetch]);

  const handleSearchChange = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 0 }));
  }, []);

  const handleGenderChange = useCallback(
    (gender: PatientsPaginationRequestDtoGenderEnum[]) => {
      setFilters((prev) => ({ ...prev, gender, page: 0 }));
    },
    []
  );

  const handleStatusChange = useCallback(
    (status: PatientsPaginationRequestDtoStatusEnum[]) => {
      setFilters((prev) => ({ ...prev, status, page: 0 }));
    },
    []
  );

  const handleAgeRangeChange = useCallback(
    (ageFrom: number | null, ageTo: number | null) => {
      setFilters((prev) => ({ ...prev, ageFrom, ageTo, page: 0 }));
    },
    []
  );

  const getStatusColor = useCallback(
    (status?: string) => {
      switch (status?.toLowerCase()) {
        case PatientsPaginationRequestDtoStatusEnum.ActiveSharing:
          return {
            border: `1px solid ${colors.success[700]}`,
            backgroundColor: colors.success[100],
            color: colors.success[700],
          };
        case PatientsPaginationRequestDtoStatusEnum.Revoked:
          return {
            border: `1px solid ${colors.error[700]}`,
            backgroundColor: colors.error[100],
            color: colors.error[700],
          };
        case PatientsPaginationRequestDtoStatusEnum.Requesting:
          return {
            border: `1px solid ${colors.warning[700]}`,
            backgroundColor: colors.warning[100],
            color: colors.warning[700],
          };
        default:
          return {
            p: 0,
            fontSize: 14,
          };
      }
    },
    [colors]
  );

  const getStatusLabel = useCallback((status?: string) => {
    switch (status?.toLowerCase()) {
      case PatientsPaginationRequestDtoStatusEnum.ActiveSharing:
        return "Active Sharing";
      case PatientsPaginationRequestDtoStatusEnum.Revoked:
        return "Revoked";
      case PatientsPaginationRequestDtoStatusEnum.Requesting:
        return "Requesting";
      default:
        return "N/A";
    }
  }, []);

  const columns: CustomTableColumn<Patient>[] = useMemo(
    () => [
      {
        id: "email",
        label: "Email",
        sortable: true,
        sortAccessor: (row: Patient) => (row.email ? row.email : ""),
        getValue: (row: Patient) => (row.email ? row.email : "N/A"),
      },
      {
        id: "fullName",
        label: "Full Name",
        sortable: true,
        sortAccessor: (row: Patient) => formatPatientName(row),
        getValue: (row: Patient) => formatPatientName(row),
      },
      {
        id: "gender",
        label: "Gender",
        getValue: (row: Patient) => (row.gender ? row.gender : "N/A"),
      },
      {
        id: "birthDate",
        label: "Birthday",
        sortable: true,
        sortAccessor: (row: Patient) =>
          row.birthDate ? new Date(row.birthDate) : "N/A",
        getValue: (row: Patient) =>
          isValid(row.birthDate) ? formatBirthDate(row.birthDate) : "N/A",
      },
      {
        id: "phone",
        label: "Phone",
        sortable: true,
        sortAccessor: (row: Patient) => (row.phone ? row.phone : "N/A"),
        getValue: (row: Patient) => (row.phone ? row.phone : "N/A"),
      },
      {
        id: "status",
        label: "Status",
        getValue: (row: Patient) => (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              borderRadius: 0.5,
              fontSize: 12,
              fontWeight: 600,
              ...getStatusColor(row.status),
            }}
          >
            {getStatusLabel(row.status)}
          </Box>
        ),
      },
      {
        id: "actions",
        label: "",
        align: "center",
        getValue: (row: Patient) => (
          <RowActions
            onViewDetails={() => {
              handleViewPatientDetails(row.patientId);
            }}
            onCancelRequest={() => {
              handleCancelRequest(row);
            }}
            showCancelRequest={
              row.status === PatientsPaginationRequestDtoStatusEnum.Requesting
            }
            onRequest={() => {
              handleRequest(row);
            }}
            showRequest={
              row.status === PatientsPaginationRequestDtoStatusEnum.Revoked
            }
          />
        ),
        width: "5%",
      },
    ],
    [
      handleViewPatientDetails,
      handleCancelRequest,
      handleRequest,
      getStatusColor,
      getStatusLabel,
    ]
  );

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowsPerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => ({
        ...prev,
        pageSize: parseInt(e.target.value, 10),
        page: 0,
      }));
    },
    []
  );

  return (
    <DashboardLayout
      title="Patient Management"
      headerActions={
        <Button
          variant="contained"
          className="rounded-full px-6 py-2 font-inter text-white transition-all duration-200 hover:shadow-lg"
          onClick={handlePatientRequest}
          sx={{
            backgroundColor: colors.primary.main,
            "&:hover": {
              backgroundColor: colors.primary.dark,
            },
          }}
        >
          Patient Request
        </Button>
      }
    >
      {/* Main Content */}
      <Box className="flex flex-col h-full">
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${colors.border.light}`,
            borderRadius: 1,
            p: 2,
          }}
        >
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {/* Search and Filters */}
          <Box className="mb-4">
            <Box className="grid grid-cols-2 gap-4 items-stretch">
              <PatientSearch
                value={filters.search}
                onChange={handleSearchChange}
              />
              <Box className="w-full flex justify-end">
                <PatientFilters
                  gender={filters.gender}
                  status={filters.status}
                  ageFrom={filters.ageFrom}
                  ageTo={filters.ageTo}
                  onGenderChange={handleGenderChange}
                  onStatusChange={handleStatusChange}
                  onAgeRangeChange={handleAgeRangeChange}
                />
              </Box>
            </Box>
          </Box>

          {/* Table */}
          <Box className="flex-1">
            <CustomTable<Patient>
              data={patients}
              columns={columns}
              totalCount={totalCount}
              page={filters.page}
              rowsPerPage={filters.pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              isLoading={loading}
              loadingMessage="Loading patients..."
              tableContainerSx={{
                height: "calc(100vh - 250px)",
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Patient Request Modal */}
      <PatientRequestModal
        open={isPatientRequestModalOpen}
        onClose={() => setIsPatientRequestModalOpen(false)}
        onSubmit={handlePatientRequestSubmit}
      />

      {/* Cancel Patient Request Modal */}
      <CancelPatientRequestModal
        open={isCancelRequestModalOpen}
        onClose={() => {
          setIsCancelRequestModalOpen(false);
          setSelectedPatientForCancel(null);
        }}
        onSubmit={handleCancelRequestSubmit}
        patientEmail={selectedPatientForCancel?.email}
      />

      {/* Request Patient Confirm Modal */}
      <RequestPatientConfirmModal
        open={isRequestConfirmModalOpen}
        onClose={() => {
          setIsRequestConfirmModalOpen(false);
          setSelectedPatientForRequest(null);
        }}
        onSubmit={handleRequestSubmit}
        patientEmail={selectedPatientForRequest?.email}
      />
    </DashboardLayout>
  );
};

export default PatientManagement;
